import { useTheme } from '@emotion/react';
import { CardMembership } from '@mui/icons-material';
import { useMemo } from 'react';
import MembershipQRCode from 'react-qr-code';

import { MarketplaceV1 } from '@jebel/constants';
import { formatShortDate, formatUserName } from '@jebel/utils';

import { useUserRewardAccount } from 'features/rewarding/hooks';
import { LoadingOverlay } from 'shared/components/ui';
import { UserInfoFragment } from 'shared/graphql';
import { useMediaPreview } from 'shared/hooks';

import {
  Container,
  Content,
  MemberName,
  MemberPoints,
  MembershipCodeContainer,
  MembershipLogo,
  MembershipStatus,
  MemberSince,
  Title,
} from './UserMembershipCard.style';

export { CONTAINER_HEIGHT as USER_MEMBERSHIP_CARD_HEIGHT } from './UserMembershipCard.style';

export interface UserMembershipCardProps {
  /**
   * The user to display.
   */
  user: UserInfoFragment | null | undefined;

  /**
   * The status of the user community membership.
   */
  status?: MarketplaceV1.UserCommunityMembershipStatus;

  /**
   * The membership to display.
   */
  membership: MarketplaceV1.CommunityMembership;
}

export function UserMembershipCard({
  user,
  status = MarketplaceV1.UserCommunityMembershipStatus.Active,
  membership,
}: UserMembershipCardProps) {
  const { src: logoSrc, srcSet: logoSrcSet } = useMediaPreview(membership.logo);
  const { palette } = useTheme();

  const { data: rewardAccount, isLoading: isRewardAccountLoading } = useUserRewardAccount(user);

  const memberSinceDate = useMemo(() => {
    return formatShortDate(membership.creationDate);
  }, [membership.creationDate]);

  const memberName = useMemo(() => {
    return formatUserName(user);
  }, [user]);

  const codeColor = useMemo(() => {
    return palette.getContrastText(membership.color);
  }, [membership.color, palette]);

  return (
    <Container className="user-membership-card-container" membershipColor={membership.color}>
      <Content>
        <MembershipLogo membershipColor={membership.color} src={logoSrc} srcSet={logoSrcSet}>
          <CardMembership fontSize="inherit" />
        </MembershipLogo>

        <Title>{membership.name}</Title>

        <MemberName>{memberName}</MemberName>

        <LoadingOverlay loading={isRewardAccountLoading}>
          <MemberPoints>{rewardAccount?.balance ?? 0} points</MemberPoints>
        </LoadingOverlay>

        <MemberSince membershipColor={membership.color}>
          Member since: {memberSinceDate}
        </MemberSince>
      </Content>

      <MembershipCodeContainer>
        <MembershipQRCode
          size={96}
          value={membership.id}
          bgColor="transparent"
          fgColor={codeColor}
        />

        <MembershipStatus membershipColor={membership.color} label={status} />
      </MembershipCodeContainer>
    </Container>
  );
}
